import { ReactNode } from "react";
import Box from "@mui/material/Box";
import { formatNumber } from "../../utils";
import { Metrics, PivotTab, StoreGroupBy } from "../../types/pivot.type";
import { PV } from "./pivotTokens";

export const fmtQty = (v: number | null | undefined) =>
  formatNumber(v, { decimals: 0 });

export const fmtMoney = (v: number | null | undefined) =>
  `฿${formatNumber(v, { decimals: 2 })}`;

/** Compact money for the KPI strip — ฿138K rather than ฿138,204.55. */
export const fmtMoneyShort = (v: number | null | undefined) => {
  if (v === null || v === undefined) return "—";
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `฿${formatNumber(v / 1_000_000, { decimals: 1 })}M`;
  if (abs >= 1_000) return `฿${formatNumber(v / 1_000, { decimals: 0 })}K`;
  return `฿${formatNumber(v, { decimals: 0 })}`;
};

export const fmtPercent = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : `${formatNumber(v, { decimals: 1 })}%`;

/** Matches the mockup's badge thresholds: <4 ok, 4-6 watch, >6 act. */
export const wastePercentTone = (v: number | null | undefined) => {
  if (v === null || v === undefined) return null;
  if (v < 4) return "ok" as const;
  if (v <= 6) return "warn" as const;
  return "bad" as const;
};

const BADGE_TONE = {
  ok: { fg: PV.sell, bg: PV.sellTint },
  warn: { fg: PV.leftover, bg: PV.leftoverTint },
  bad: { fg: PV.waste, bg: PV.wasteTint },
} as const;

/**
 * Small tinted pill for %ของเสีย (mockup `.badge`). A `null` value — what the API returns
 * when ขาย + ของเสีย is 0, or when nothing in scope recorded ของเสีย at all — renders as a
 * bare dash, never a badge: a green "0.0%" pill would assert perfect sell-through that
 * nobody measured.
 */
export const WastePercentBadge = ({ value }: { value: number | null }) => {
  const tone = wastePercentTone(value);
  if (!tone) return <Box component="span" sx={{ color: PV.tx3 }}>—</Box>;
  const { fg, bg } = BADGE_TONE[tone];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        fontSize: 12,
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
        borderRadius: "6px",
        px: 1.1,
        py: 0.3,
        bgcolor: bg,
        color: fg,
      }}
    >
      {fmtPercent(value)}
    </Box>
  );
};

/** Plain colored value; dash when the metric has no measurement behind it. */
const Value = ({
  value,
  color,
  bold,
  format = fmtQty,
}: {
  value: number | null;
  color?: string;
  bold?: boolean;
  format?: (v: number | null) => string;
}) => {
  if (value === null || value === undefined)
    return <Box component="span" sx={{ color: PV.tx3 }}>—</Box>;
  return (
    <Box component="span" sx={{ color, fontWeight: bold ? 600 : undefined }}>
      {format(value)}
    </Box>
  );
};

/**
 * A metric column. There is deliberately no per-column capability flag: the API already
 * sends `null` for anything nothing recorded, and `Value` renders null as "—", so a flag
 * would only be a second, drift-prone copy of what the payload already says. (The previous
 * `shelfDependent` field was exactly that, and nothing ever read it.) The capability flags
 * drive the KPI tiles and the warning banner, where there is no cell to leave blank.
 */
export interface MetricColumn {
  key: keyof Metrics;
  label: string;
  align: "right" | "center";
  render: (v: number | null) => ReactNode;
}

export const METRIC_COLUMNS: MetricColumn[] = [
  {
    key: "visit_count",
    label: "รอบ",
    align: "center",
    render: (v) => <Value value={v} />,
  },
  {
    key: "sold_qty",
    label: "ขาย (ชิ้น)",
    align: "right",
    render: (v) => <Value value={v} />,
  },
  {
    key: "sold_percent",
    label: "%ของขาย",
    align: "center",
    render: (v) => <Value value={v} color={PV.sell} bold format={fmtPercent} />,
  },
  {
    key: "waste_qty",
    label: "ของเสีย (ชิ้น)",
    align: "right",
    render: (v) => <Value value={v} color={PV.waste} />,
  },
  {
    key: "waste_percent",
    label: "%ของเสีย",
    align: "center",
    render: (v) => <WastePercentBadge value={v} />,
  },
  {
    key: "carryover_qty",
    label: "เหลือบนเชลล์ (ชิ้น)",
    align: "right",
    render: (v) => <Value value={v} color={PV.leftover} />,
  },
  {
    key: "sales_amount",
    label: "ยอดขาย (บาท)",
    align: "right",
    render: (v) => <Value value={v} color={PV.tx} bold format={fmtMoney} />,
  },
];

/**
 * Visit tab keeps only the ratio/money columns on screen — the raw piece counts give way to
 * the attendance columns (เข้าจริง/ตามแผน · พลาด · ห่างจากรอบล่าสุด), matching the mockup's
 * calendar sub-view (`PVVCNUM`). The hidden ones still ship in the CSV export.
 */
export const VISIT_METRIC_COLUMNS = METRIC_COLUMNS.filter((c) =>
  ["sold_percent", "waste_percent", "sales_amount"].includes(c.key),
);

export const GROUP_LABEL: Record<StoreGroupBy, string> = {
  customer_type: "กลุ่มลูกค้า",
  customer_group: "กลุ่มร้านค้า",
  route: "สายส่ง",
  branch: "สาขา",
};

export const PIVOT_TAB_LABEL: Record<PivotTab, string> = {
  ctype: "รายร้าน · แบ่งตามกลุ่มลูกค้า",
  route: "รายร้าน · แบ่งตามสายส่ง",
  branch: "รายร้าน · แบ่งตามสาขา",
  visit: "รอบวิ่ง · การเข้าร้านค้า",
  sku: "รายสินค้า · แบ่งตามกลุ่มลูกค้า",
  routetime: "เวลาวิ่งสาย · PWA",
};

/** Tabs that still carry a "ใหม่" pill on the tab strip. */
export const PIVOT_NEW_TABS: PivotTab[] = ["routetime"];

export const rowUnit = (tab: PivotTab) => (tab === "sku" ? "รายการ" : "ร้าน");

export const THAI_WEEKDAY_SHORT = ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"];

/** Monospace-ish code pill used for store / product codes (mockup `.code`). */
export const CodeChip = ({ children }: { children: ReactNode }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      fontSize: 11.5,
      fontVariantNumeric: "tabular-nums",
      color: PV.indigo,
      bgcolor: PV.indigoTint,
      px: 0.9,
      py: 0.25,
      borderRadius: "5px",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </Box>
);
